<template>
  <div class="page-chantiers">
    <landing-header />

    <main>

      <!-- ===== HERO ===== -->
      <section class="hero-chantiers">
        <div class="max-w-7xl mx-auto px-4 flex flex-col lg:flex-row items-center gap-16">
          <div class="lg:w-1/2">
            <span class="badge-chantier">Gestion de chantier</span>
            <h1 class="text-4xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
              Tous vos chantiers,<br />
              <span class="gradient-text-blue">pilotés depuis un seul endroit</span>
            </h1>
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              Clients, locataires, régies, techniciens, interventions, planning, documents, facturation — chaque chantier devient un dossier vivant, accessible en temps réel, du premier devis à la dernière facture soldée.
            </p>
            <div class="flex flex-wrap gap-4">
              <a href="/contact"><button class="btn-primary">Demander une démonstration</button></a>
              <a href="/fonctionnalites" class="btn-outline-large">Autres fonctionnalités</a>
            </div>
            <div class="hero-stats">
              <div class="stat-pill">
                <span class="stat-val">360°</span>
                <span class="stat-lab">Vue complète chantier</span>
              </div>
              <div class="stat-pill">
                <span class="stat-val">Multi</span>
                <span class="stat-lab">Techniciens &amp; appareils</span>
              </div>
              <div class="stat-pill">
                <span class="stat-val">Temps réel</span>
                <span class="stat-lab">Depuis le terrain</span>
              </div>
            </div>
          </div>

          <!-- Chantier fiche mockup -->
          <div class="lg:w-1/2 w-full">
            <div class="chantier-ui shadow-2xl">
              <div class="cui-header">
                <div class="cui-header-left">
                  <span class="cui-ref">CH-2025-047</span>
                  <span class="cui-title">Remplacement chaudière + 2 clims</span>
                </div>
                <span class="cui-status en-cours">En cours</span>
              </div>
              <div class="cui-body">
                <div class="cui-map-placeholder">
                  <div class="map-pin-anim">
                    <i class="fa-solid fa-location-dot"></i>
                  </div>
                  <div class="map-addr">
                    <strong>12 rue des Marronniers</strong>
                    <small>69003 Lyon — 3e arr.</small>
                  </div>
                  <span class="map-link">Ouvrir dans Maps <i class="fa-solid fa-arrow-up-right-from-square"></i></span>
                </div>
                <div class="cui-parties">
                  <div class="cui-party">
                    <div class="party-icon client"><i class="fa-solid fa-user-tie"></i></div>
                    <div>
                      <span class="party-role">Propriétaire</span>
                      <span class="party-name">SCI Lumière &amp; Fils</span>
                    </div>
                  </div>
                  <div class="cui-party">
                    <div class="party-icon regie"><i class="fa-solid fa-building"></i></div>
                    <div>
                      <span class="party-role">Régie</span>
                      <span class="party-name">Foncia Lyon Centre</span>
                    </div>
                  </div>
                  <div class="cui-party">
                    <div class="party-icon locataire"><i class="fa-solid fa-person"></i></div>
                    <div>
                      <span class="party-role">Locataire</span>
                      <span class="party-name">M. &amp; Mme Fontaine</span>
                    </div>
                  </div>
                </div>
                <div class="cui-quick-stats">
                  <div class="qs"><span class="qs-val">3</span><span class="qs-lab">Interventions</span></div>
                  <div class="qs"><span class="qs-val">2</span><span class="qs-lab">Techniciens</span></div>
                  <div class="qs"><span class="qs-val">4 250 €</span><span class="qs-lab">Facturé</span></div>
                  <div class="qs"><span class="qs-val">87%</span><span class="qs-lab">Rentabilité</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== BARRE DE CONFIANCE ===== -->
      <section class="trust-bar">
        <div class="max-w-7xl mx-auto px-4">
          <div class="trust-items">
            <div class="trust-item"><i class="fa-solid fa-map-location-dot"></i><span>Localisation sur carte</span></div>
            <div class="trust-sep"></div>
            <div class="trust-item"><i class="fa-solid fa-users"></i><span>Clients, régies, locataires</span></div>
            <div class="trust-sep"></div>
            <div class="trust-item"><i class="fa-solid fa-calendar-check"></i><span>Planning multi-techniciens</span></div>
            <div class="trust-sep"></div>
            <div class="trust-item"><i class="fa-solid fa-folder-open"></i><span>Documents centralisés</span></div>
            <div class="trust-sep"></div>
            <div class="trust-item"><i class="fa-solid fa-chart-line"></i><span>Rentabilité en temps réel</span></div>
          </div>
        </div>
      </section>

      <!-- ===== SECTION CARTE & CONTACTS ===== -->
      <section class="py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4">
          <div class="flex flex-col lg:flex-row gap-16 items-start">
            <div class="lg:w-1/2">
              <div class="section-label">Localisation &amp; contacts</div>
              <h2 class="section-title">L'adresse du chantier sur la carte,<br />tous les interlocuteurs en un clic</h2>
              <p class="section-sub mb-10">Chaque chantier centralise l'ensemble des parties prenantes : propriétaire, régie, locataire, contact de chantier. Finis les post-its et les numéros perdus dans les SMS.</p>

              <div class="contact-features">
                <div class="contact-feat">
                  <div class="cf-icon map"><i class="fa-solid fa-map-location-dot"></i></div>
                  <div>
                    <h4>Carte interactive intégrée</h4>
                    <p>Visualisez l'adresse du chantier directement dans Termoflow. Ouvrez l'itinéraire en un clic dans Google Maps, Waze ou Plans depuis votre téléphone, depuis le bureau ou depuis l'application de vos techniciens.</p>
                  </div>
                </div>
                <div class="contact-feat">
                  <div class="cf-icon client"><i class="fa-solid fa-user-tie"></i></div>
                  <div>
                    <h4>Fiche propriétaire / donneur d'ordre</h4>
                    <p>Nom, adresse, téléphone, email, SIRET pour les professionnels. Toutes les informations sont pré-remplies sur vos devis et factures dès la création du chantier.</p>
                  </div>
                </div>
                <div class="contact-feat">
                  <div class="cf-icon regie"><i class="fa-solid fa-building"></i></div>
                  <div>
                    <h4>Régie rattachée</h4>
                    <p>Rattachez une agence de gestion immobilière au chantier. Ses coordonnées apparaissent automatiquement pour les envois de rapports et de factures.</p>
                  </div>
                </div>
                <div class="contact-feat">
                  <div class="cf-icon locataire"><i class="fa-solid fa-person"></i></div>
                  <div>
                    <h4>Coordonnées du locataire</h4>
                    <p>Numéro de téléphone, email, créneaux préférés pour les interventions. Vos techniciens savent qui appeler sur place, sans passer par le bureau.</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="lg:w-1/2">
              <!-- Map + contacts mockup -->
              <div class="map-contacts-card">
                <div class="map-visual">
                  <div class="map-bg">
                    <div class="map-grid"></div>
                    <div class="map-road h"></div>
                    <div class="map-road v"></div>
                    <div class="map-road h2"></div>
                    <div class="map-road v2"></div>
                    <div class="map-block b1"></div>
                    <div class="map-block b2"></div>
                    <div class="map-block b3"></div>
                    <div class="map-block b4"></div>
                    <div class="map-pin-wrap">
                      <div class="map-pin-dot">
                        <i class="fa-solid fa-location-dot"></i>
                      </div>
                      <div class="map-tooltip">
                        <strong>12 rue des Marronniers</strong>
                        <span>69003 Lyon</span>
                        <a class="map-itinerary">Itinéraire <i class="fa-solid fa-arrow-up-right-from-square"></i></a>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="contacts-list">
                  <div class="contact-row">
                    <div class="contact-avatar client-av">SL</div>
                    <div class="contact-info">
                      <span class="contact-role">Propriétaire</span>
                      <span class="contact-name">SCI Lumière &amp; Fils</span>
                      <span class="contact-detail">contact@sci-lumiere.fr — 04 78 XX XX XX</span>
                    </div>
                    <div class="contact-actions">
                      <button class="ca-btn"><i class="fa-solid fa-phone"></i></button>
                      <button class="ca-btn"><i class="fa-solid fa-envelope"></i></button>
                    </div>
                  </div>
                  <div class="contact-row">
                    <div class="contact-avatar regie-av">FC</div>
                    <div class="contact-info">
                      <span class="contact-role">Régie</span>
                      <span class="contact-name">Foncia Lyon Centre</span>
                      <span class="contact-detail">M. Renard — 04 72 XX XX XX</span>
                    </div>
                    <div class="contact-actions">
                      <button class="ca-btn"><i class="fa-solid fa-phone"></i></button>
                      <button class="ca-btn"><i class="fa-solid fa-envelope"></i></button>
                    </div>
                  </div>
                  <div class="contact-row">
                    <div class="contact-avatar locataire-av">MF</div>
                    <div class="contact-info">
                      <span class="contact-role">Locataire</span>
                      <span class="contact-name">M. &amp; Mme Fontaine</span>
                      <span class="contact-detail">06 XX XX XX XX — Disponible 9h–18h</span>
                    </div>
                    <div class="contact-actions">
                      <button class="ca-btn"><i class="fa-solid fa-phone"></i></button>
                      <button class="ca-btn"><i class="fa-solid fa-comment"></i></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== SECTION INTERVENTIONS ===== -->
      <section class="py-24 bg-gray-50 border-y border-gray-100">
        <div class="max-w-7xl mx-auto px-4">
          <div class="flex flex-col lg:flex-row-reverse gap-16 items-start">
            <div class="lg:w-1/2">
              <div class="section-label">Gestion des interventions</div>
              <h2 class="section-title">Un chantier, plusieurs interventions,<br />tout suivi depuis le même dossier</h2>
              <p class="section-sub mb-10">Dépannage urgent, installation, entretien annuel — chaque intervention est un événement traçable, rattaché à l'appareil concerné, avec son propre rapport, ses pièces, son compte rendu.</p>

              <div class="interv-features">
                <div class="interv-feat">
                  <div class="if-icon"><i class="fa-solid fa-fire-burner"></i></div>
                  <div>
                    <h4>Par type d'intervention</h4>
                    <p>Dépannage, installation, entretien, mise en service, diagnostic — chaque intervention est typée pour un suivi clair et des statistiques pertinentes.</p>
                  </div>
                </div>
                <div class="interv-feat">
                  <div class="if-icon"><i class="fa-solid fa-wrench"></i></div>
                  <div>
                    <h4>Rattachée à un appareil</h4>
                    <p>Liez chaque intervention à l'équipement concerné (chaudière, clim salon, PAC). L'historique par appareil est complet et consultable à tout moment.</p>
                  </div>
                </div>
                <div class="interv-feat">
                  <div class="if-icon"><i class="fa-solid fa-circle-info"></i></div>
                  <div>
                    <h4>Suivi détaillé</h4>
                    <p>Durée réelle, pièces posées, photos terrain, rapport de l'intervention, signature client — tout est centralisé. <a href="/interventions" class="link-blue">En savoir plus sur /interventions →</a></p>
                  </div>
                </div>
                <div class="interv-feat">
                  <div class="if-icon"><i class="fa-solid fa-triangle-exclamation"></i></div>
                  <div>
                    <h4>Statut en temps réel</h4>
                    <p>Planifiée, en cours, terminée, à facturer — le statut de chaque intervention est mis à jour par le technicien depuis l'appli mobile, visible immédiatement au bureau.</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="lg:w-1/2">
              <div class="interventions-list-card">
                <div class="ilc-header">
                  <span>Interventions du chantier CH-2025-047</span>
                  <button class="ilc-add">+ Nouvelle</button>
                </div>
                <!-- Intervention 1 -->
                <div class="interv-item done">
                  <div class="interv-status-dot done"></div>
                  <div class="interv-content">
                    <div class="interv-top">
                      <span class="interv-type depannage">Dépannage</span>
                      <span class="interv-date">2 mai 2025 — 9h30</span>
                    </div>
                    <div class="interv-title">Remplacement vanne 3 voies chaudière VIESSMAN</div>
                    <div class="interv-meta">
                      <span><i class="fa-solid fa-user-helmet-safety"></i> J. Marchand</span>
                      <span><i class="fa-solid fa-clock"></i> 2h15</span>
                      <span><i class="fa-solid fa-fire-burner"></i> Chaudière murale 24kW</span>
                    </div>
                    <div class="interv-footer-row">
                      <span class="interv-badge done-badge">Terminée</span>
                      <a href="/interventions" class="interv-link">Voir le détail →</a>
                    </div>
                  </div>
                </div>
                <!-- Intervention 2 -->
                <div class="interv-item inprog">
                  <div class="interv-status-dot inprog"></div>
                  <div class="interv-content">
                    <div class="interv-top">
                      <span class="interv-type installation">Installation</span>
                      <span class="interv-date">14 mai 2025 — 8h00</span>
                    </div>
                    <div class="interv-title">Installation clim DAIKIN Perfera — Séjour</div>
                    <div class="interv-meta">
                      <span><i class="fa-solid fa-user-helmet-safety"></i> J. Marchand + L. Petit</span>
                      <span><i class="fa-solid fa-clock"></i> 4h (en cours)</span>
                      <span><i class="fa-solid fa-snowflake"></i> Clim séjour</span>
                    </div>
                    <div class="interv-footer-row">
                      <span class="interv-badge inprog-badge">En cours</span>
                      <a href="/interventions" class="interv-link">Voir le détail →</a>
                    </div>
                  </div>
                </div>
                <!-- Intervention 3 -->
                <div class="interv-item planned">
                  <div class="interv-status-dot planned"></div>
                  <div class="interv-content">
                    <div class="interv-top">
                      <span class="interv-type installation">Installation</span>
                      <span class="interv-date">15 mai 2025 — 14h00</span>
                    </div>
                    <div class="interv-title">Installation clim DAIKIN Perfera — Chambre parentale</div>
                    <div class="interv-meta">
                      <span><i class="fa-solid fa-user-helmet-safety"></i> L. Petit</span>
                      <span><i class="fa-solid fa-clock"></i> ~3h</span>
                      <span><i class="fa-solid fa-snowflake"></i> Clim chambre</span>
                    </div>
                    <div class="interv-footer-row">
                      <span class="interv-badge planned-badge">Planifiée</span>
                      <a href="/interventions" class="interv-link">Voir le détail →</a>
                    </div>
                  </div>
                </div>
                <div class="ilc-footer">
                  <a href="/interventions" class="ilc-see-all">Voir toutes les interventions et leurs détails →</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== SECTION PLANNING ===== -->
      <section class="section-dark py-24">
        <div class="max-w-7xl mx-auto px-4">
          <div class="text-center max-w-3xl mx-auto mb-16">
            <div class="section-label-light">Planning</div>
            <h2 class="text-3xl font-bold text-white mb-4">Un planning de chantier lisible,<br />même avec 5 techniciens et 10 interventions</h2>
            <p class="text-slate-400 leading-relaxed">Affectez vos techniciens, posez des créneaux, visualisez les conflits, réorganisez en glisser-déposer. Chacun voit son planning sur son téléphone dès la mise à jour.</p>
          </div>

          <div class="planning-mockup">
            <div class="planning-header">
              <div class="planning-nav">
                <button class="pnav-btn"><i class="fa-solid fa-chevron-left"></i></button>
                <span class="pnav-title">Semaine du 12 au 18 mai 2025</span>
                <button class="pnav-btn"><i class="fa-solid fa-chevron-right"></i></button>
              </div>
              <div class="planning-legend">
                <span class="leg-item"><span class="leg-dot marchand"></span> J. Marchand</span>
                <span class="leg-item"><span class="leg-dot petit"></span> L. Petit</span>
                <span class="leg-item"><span class="leg-dot bernard"></span> S. Bernard</span>
              </div>
            </div>
            <div class="planning-grid">
              <div class="pg-days">
                <div class="pg-day-label">Lun 12</div>
                <div class="pg-day-label">Mar 13</div>
                <div class="pg-day-label">Mer 14</div>
                <div class="pg-day-label">Jeu 15</div>
                <div class="pg-day-label">Ven 16</div>
              </div>
              <div class="pg-technicians">
                <!-- Marchand -->
                <div class="pgt-row">
                  <div class="pgt-name">J. Marchand</div>
                  <div class="pgt-cells">
                    <div class="pgt-cell">
                      <div class="pgt-event marchand-ev">
                        <span class="pev-title">Entretien PAC</span>
                        <span class="pev-addr">Rue Voltaire</span>
                        <span class="pev-time">8h–11h</span>
                      </div>
                    </div>
                    <div class="pgt-cell">
                      <div class="pgt-event marchand-ev">
                        <span class="pev-title">Dépannage chaud.</span>
                        <span class="pev-addr">Rue Carnot</span>
                        <span class="pev-time">9h–12h</span>
                      </div>
                    </div>
                    <div class="pgt-cell">
                      <div class="pgt-event marchand-ev active-ev">
                        <span class="pev-badge">● En cours</span>
                        <span class="pev-title">Install. clim séjour</span>
                        <span class="pev-addr">CH-2025-047</span>
                        <span class="pev-time">8h–12h</span>
                      </div>
                    </div>
                    <div class="pgt-cell empty-cell"></div>
                    <div class="pgt-cell">
                      <div class="pgt-event marchand-ev">
                        <span class="pev-title">VMC tertiaire</span>
                        <span class="pev-addr">Rue de la Paix</span>
                        <span class="pev-time">8h–17h</span>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Petit -->
                <div class="pgt-row">
                  <div class="pgt-name">L. Petit</div>
                  <div class="pgt-cells">
                    <div class="pgt-cell empty-cell"></div>
                    <div class="pgt-cell">
                      <div class="pgt-event petit-ev">
                        <span class="pev-title">Mise en service PAC</span>
                        <span class="pev-addr">Bron</span>
                        <span class="pev-time">14h–17h</span>
                      </div>
                    </div>
                    <div class="pgt-cell">
                      <div class="pgt-event petit-ev active-ev">
                        <span class="pev-badge">● En cours</span>
                        <span class="pev-title">Install. clim séjour</span>
                        <span class="pev-addr">CH-2025-047</span>
                        <span class="pev-time">8h–12h</span>
                      </div>
                    </div>
                    <div class="pgt-cell">
                      <div class="pgt-event petit-ev planned-ev">
                        <span class="pev-badge planned-badge-ev">◎ Planifiée</span>
                        <span class="pev-title">Install. clim chambre</span>
                        <span class="pev-addr">CH-2025-047</span>
                        <span class="pev-time">14h–17h</span>
                      </div>
                    </div>
                    <div class="pgt-cell empty-cell"></div>
                  </div>
                </div>
                <!-- Bernard -->
                <div class="pgt-row">
                  <div class="pgt-name">S. Bernard</div>
                  <div class="pgt-cells">
                    <div class="pgt-cell">
                      <div class="pgt-event bernard-ev">
                        <span class="pev-title">Entretien clim</span>
                        <span class="pev-addr">Villeurbanne</span>
                        <span class="pev-time">9h–11h</span>
                      </div>
                    </div>
                    <div class="pgt-cell">
                      <div class="pgt-event bernard-ev">
                        <span class="pev-title">Entretien clim</span>
                        <span class="pev-addr">Caluire</span>
                        <span class="pev-time">9h–11h</span>
                      </div>
                    </div>
                    <div class="pgt-cell empty-cell"></div>
                    <div class="pgt-cell">
                      <div class="pgt-event bernard-ev">
                        <span class="pev-title">Diag. fuite frigo.</span>
                        <span class="pev-addr">Écully</span>
                        <span class="pev-time">10h–12h</span>
                      </div>
                    </div>
                    <div class="pgt-cell empty-cell"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="planning-feats-grid">
            <div class="pf-card">
              <div class="pf-icon"><i class="fa-solid fa-user-group"></i></div>
              <h4>Multi-techniciens</h4>
              <p>Plusieurs techniciens sur le même créneau, le même chantier. Les conflits de planning sont détectés automatiquement.</p>
            </div>
            <div class="pf-card">
              <div class="pf-icon"><i class="fa-solid fa-mobile-screen-button"></i></div>
              <h4>Mis à jour en temps réel</h4>
              <p>Dès qu'un créneau est modifié, chaque technicien voit son planning actualisé sur son application mobile.</p>
            </div>
            <div class="pf-card">
              <div class="pf-icon"><i class="fa-solid fa-gears"></i></div>
              <h4>Par appareil &amp; par type</h4>
              <p>Filtrez le planning par technicien, par chantier, par type d'intervention ou par équipement pour une vision précise.</p>
            </div>
            <div class="pf-card">
              <div class="pf-icon"><i class="fa-solid fa-bell"></i></div>
              <h4>Rappels automatiques</h4>
              <p>Termoflow envoie un rappel au technicien et au locataire la veille de chaque intervention planifiée.</p>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== SECTION DOCUMENTS ===== -->
      <section class="py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4">
          <div class="flex flex-col lg:flex-row gap-16 items-start">
            <div class="lg:w-1/2">
              <div class="section-label">Documents</div>
              <h2 class="section-title">Tous les documents du chantier<br />dans un seul dossier numérique</h2>
              <p class="section-sub mb-10">Devis, factures, rapports d'intervention, CERFA, attestations, photos — tout est centralisé sur le chantier. Fini les emails introuvables et les PDF égarés.</p>

              <div class="doc-features">
                <div class="doc-feat">
                  <div class="df-icon generated"><i class="fa-solid fa-file-invoice"></i></div>
                  <div>
                    <h4>Documents générés automatiquement</h4>
                    <p>Devis, factures d'acompte, factures de solde, rapports d'intervention, fiches de maintenance, CERFA fluides — générés par Termoflow et archivés instantanément sur le chantier.</p>
                  </div>
                </div>
                <div class="doc-feat">
                  <div class="df-icon upload"><i class="fa-solid fa-cloud-arrow-up"></i></div>
                  <div>
                    <h4>Upload de documents annexes</h4>
                    <p>Attestation RGE, notice constructeur, photo avant/après chantier, bon de livraison, contrat de sous-traitance — importez n'importe quel fichier depuis votre téléphone ou votre bureau.</p>
                  </div>
                </div>
                <div class="doc-feat">
                  <div class="df-icon share"><i class="fa-solid fa-share-nodes"></i></div>
                  <div>
                    <h4>Partage en un clic</h4>
                    <p>Envoyez un dossier complet ou un document précis à votre client, à la régie ou à votre comptable par email ou lien sécurisé, sans sortir de Termoflow.</p>
                  </div>
                </div>
                <div class="doc-feat">
                  <div class="df-icon secure"><i class="fa-solid fa-shield-halved"></i></div>
                  <div>
                    <h4>Archivage sécurisé 6 ans</h4>
                    <p>Conformément aux obligations légales, tous les documents fiscaux sont conservés de façon inaltérable et consultables à tout moment, même des années après la fin du chantier.</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="lg:w-1/2">
              <div class="documents-card">
                <div class="dc-header">
                  <span>Documents — CH-2025-047</span>
                  <button class="dc-upload-btn"><i class="fa-solid fa-cloud-arrow-up"></i> Importer</button>
                </div>
                <div class="dc-filters">
                  <span class="dc-filter active">Tous</span>
                  <span class="dc-filter">Devis &amp; Factures</span>
                  <span class="dc-filter">Rapports</span>
                  <span class="dc-filter">Annexes</span>
                </div>
                <div class="doc-list">
                  <div class="doc-item">
                    <div class="doc-icon devis-icon"><i class="fa-solid fa-file-lines"></i></div>
                    <div class="doc-info">
                      <span class="doc-name">DEV-2025-089 — Remplacement chaudière + 2 clims</span>
                      <span class="doc-meta">Généré · 28 avr. 2025 · PDF · 148 ko</span>
                    </div>
                    <div class="doc-actions">
                      <button class="doc-btn"><i class="fa-solid fa-eye"></i></button>
                      <button class="doc-btn"><i class="fa-solid fa-share-nodes"></i></button>
                    </div>
                  </div>
                  <div class="doc-item">
                    <div class="doc-icon facture-icon"><i class="fa-solid fa-file-invoice-dollar"></i></div>
                    <div class="doc-info">
                      <span class="doc-name">FAC-2025-041 — Acompte 30%</span>
                      <span class="doc-meta">Généré · 2 mai 2025 · PDF · Scellé SHA-256</span>
                    </div>
                    <div class="doc-actions">
                      <button class="doc-btn"><i class="fa-solid fa-eye"></i></button>
                      <button class="doc-btn"><i class="fa-solid fa-share-nodes"></i></button>
                    </div>
                  </div>
                  <div class="doc-item">
                    <div class="doc-icon rapport-icon"><i class="fa-solid fa-clipboard-check"></i></div>
                    <div class="doc-info">
                      <span class="doc-name">Rapport d'intervention — Dépannage vanne 3 voies</span>
                      <span class="doc-meta">Généré · 2 mai 2025 · PDF · Signé</span>
                    </div>
                    <div class="doc-actions">
                      <button class="doc-btn"><i class="fa-solid fa-eye"></i></button>
                      <button class="doc-btn"><i class="fa-solid fa-share-nodes"></i></button>
                    </div>
                  </div>
                  <div class="doc-item">
                    <div class="doc-icon cerfa-icon"><i class="fa-solid fa-file-shield"></i></div>
                    <div class="doc-info">
                      <span class="doc-name">CERFA fluides — Installation clim séjour</span>
                      <span class="doc-meta">Généré · 14 mai 2025 · PDF</span>
                    </div>
                    <div class="doc-actions">
                      <button class="doc-btn"><i class="fa-solid fa-eye"></i></button>
                      <button class="doc-btn"><i class="fa-solid fa-share-nodes"></i></button>
                    </div>
                  </div>
                  <div class="doc-item uploaded-item">
                    <div class="doc-icon upload-icon"><i class="fa-solid fa-image"></i></div>
                    <div class="doc-info">
                      <span class="doc-name">Photos avant chantier (×8)</span>
                      <span class="doc-meta">Importé · 28 avr. 2025 · ZIP · 12 Mo</span>
                    </div>
                    <div class="doc-actions">
                      <button class="doc-btn"><i class="fa-solid fa-eye"></i></button>
                      <button class="doc-btn"><i class="fa-solid fa-share-nodes"></i></button>
                    </div>
                  </div>
                  <div class="doc-item uploaded-item">
                    <div class="doc-icon upload-icon"><i class="fa-solid fa-certificate"></i></div>
                    <div class="doc-info">
                      <span class="doc-name">Attestation RGE — QualiPAC 2025</span>
                      <span class="doc-meta">Importé · 1 janv. 2025 · PDF · 340 ko</span>
                    </div>
                    <div class="doc-actions">
                      <button class="doc-btn"><i class="fa-solid fa-eye"></i></button>
                      <button class="doc-btn"><i class="fa-solid fa-share-nodes"></i></button>
                    </div>
                  </div>
                </div>
                <div class="dc-footer">
                  <span>8 documents · 14,2 Mo</span>
                  <button class="dc-dl-btn"><i class="fa-solid fa-download"></i> Tout télécharger</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== SECTION FACTURATION & RENTABILITÉ ===== -->
      <section class="py-24 bg-gray-50 border-t border-gray-100">
        <div class="max-w-7xl mx-auto px-4">
          <div class="text-center max-w-3xl mx-auto mb-16">
            <div class="section-label">Facturation &amp; Rentabilité</div>
            <h2 class="section-title">Sachez si votre chantier est<br />rentable, en temps réel</h2>
            <p class="section-sub">Termoflow agrège automatiquement toutes les dépenses et recettes du chantier : heures techniciens, matériaux, déplacements, factures émises et encaissées. Votre marge nette s'affiche sans calcul de votre part.</p>
          </div>

          <div class="rentabilite-layout">
            <!-- Gauche : stats -->
            <div class="renta-dashboard">
              <div class="renta-header">Rentabilité — CH-2025-047</div>
              <div class="renta-main-kpi">
                <div class="renta-gauge-wrap">
                  <div class="renta-gauge">
                    <svg viewBox="0 0 120 70" class="gauge-svg">
                      <path d="M10,65 A55,55 0 0,1 110,65" fill="none" stroke="#e5e7eb" stroke-width="10" stroke-linecap="round"/>
                      <path d="M10,65 A55,55 0 0,1 110,65" fill="none" stroke="#f97316" stroke-width="10" stroke-linecap="round" stroke-dasharray="172" stroke-dashoffset="22" class="gauge-fill"/>
                    </svg>
                    <div class="gauge-label">
                      <span class="gauge-val">87%</span>
                      <span class="gauge-sub">Rentabilité</span>
                    </div>
                  </div>
                </div>
                <div class="renta-status good">
                  <i class="fa-solid fa-circle-check"></i> Chantier rentable — dans vos objectifs
                </div>
              </div>

              <div class="renta-kpis">
                <div class="renta-kpi">
                  <span class="rk-label">CA total facturé</span>
                  <span class="rk-val green">6 240 €</span>
                </div>
                <div class="renta-kpi">
                  <span class="rk-label">Coût matériaux</span>
                  <span class="rk-val">3 120 €</span>
                </div>
                <div class="renta-kpi">
                  <span class="rk-label">Heures techniciens</span>
                  <span class="rk-val">9h15 — 462,50 €</span>
                </div>
                <div class="renta-kpi">
                  <span class="rk-label">Marge brute</span>
                  <span class="rk-val orange">2 657,50 €</span>
                </div>
                <div class="renta-kpi">
                  <span class="rk-label">Encaissé</span>
                  <span class="rk-val green">4 250 € (68%)</span>
                </div>
                <div class="renta-kpi">
                  <span class="rk-label">Reste à facturer</span>
                  <span class="rk-val blue">1 990 €</span>
                </div>
              </div>

              <div class="renta-progress-section">
                <div class="rp-label">Avancement facturation</div>
                <div class="rp-bar">
                  <div class="rp-fill encaisse" style="width:68%"></div>
                  <div class="rp-fill reste" style="width:32%"></div>
                </div>
                <div class="rp-legend">
                  <span><span class="rp-dot encaisse-dot"></span> Encaissé (68%)</span>
                  <span><span class="rp-dot reste-dot"></span> Reste à facturer (32%)</span>
                </div>
              </div>
            </div>

            <!-- Droite : features -->
            <div class="renta-features">
              <div class="rf-card">
                <div class="rf-icon"><i class="fa-solid fa-coins"></i></div>
                <h4>Coûts réels vs estimés</h4>
                <p>Comparez automatiquement le coût estimé au devis avec les dépenses réelles (matériaux achetés, heures passées). Identifiez les dérives avant qu'elles ne creusent votre marge.</p>
              </div>
              <div class="rf-card">
                <div class="rf-icon"><i class="fa-solid fa-clock"></i></div>
                <h4>Suivi du temps techniciens</h4>
                <p>Chaque intervention est chronométrée. Le coût horaire réel est agrégé sur le chantier et comparé à votre tarif facturé pour mesurer précisément votre marge main d'œuvre.</p>
              </div>
              <div class="rf-card">
                <div class="rf-icon"><i class="fa-solid fa-file-invoice-dollar"></i></div>
                <h4>Facturation intégrée</h4>
                <p>Créez et gérez les factures du chantier sans quitter la fiche. Les acomptes et les soldes sont calculés automatiquement en fonction de l'avancement des interventions.</p>
              </div>
              <div class="rf-card">
                <div class="rf-icon"><i class="fa-solid fa-chart-bar"></i></div>
                <h4>Statistiques par chantier</h4>
                <p>Marge brute, taux de rentabilité, délai de paiement moyen, ratio matériaux/main d'œuvre — tous les indicateurs pour piloter votre activité avec précision.</p>
              </div>
              <div class="rf-card">
                <div class="rf-icon"><i class="fa-solid fa-bell"></i></div>
                <h4>Alertes de dérive</h4>
                <p>Si les coûts réels dépassent l'estimé de plus de 10%, Termoflow vous alerte pour que vous puissiez agir avant que le chantier ne devienne déficitaire.</p>
              </div>
              <div class="rf-card">
                <div class="rf-icon"><i class="fa-solid fa-rotate"></i></div>
                <h4>Bilan de clôture automatique</h4>
                <p>À la dernière intervention soldée, Termoflow génère un bilan complet du chantier : heures, matériaux, marge finale, délai moyen — exportable pour votre comptable.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== SECTION VUE D'ENSEMBLE CHANTIERS ===== -->
      <section class="py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4">
          <div class="text-center max-w-3xl mx-auto mb-16">
            <div class="section-label">Vue d'ensemble</div>
            <h2 class="section-title">Tous vos chantiers d'un seul regard</h2>
            <p class="section-sub">Le tableau de bord chantiers vous donne une vision globale de votre activité : chantiers en cours, en retard, à facturer, clôturés — filtrés par période, par technicien, par type.</p>
          </div>

          <div class="chantiers-overview">
            <div class="co-filters">
              <span class="cof active">Tous (24)</span>
              <span class="cof">En cours (8)</span>
              <span class="cof">À facturer (5)</span>
              <span class="cof">En retard (2)</span>
              <span class="cof">Clôturés (9)</span>
            </div>
            <div class="co-list">
              <div class="co-row active-ch">
                <div class="co-ref">CH-2025-047</div>
                <div class="co-title">Remplacement chaudière + 2 clims</div>
                <div class="co-client">SCI Lumière &amp; Fils · Lyon 3e</div>
                <div class="co-techs">
                  <span class="co-tech-av" style="background:#dbeafe;color:#1e40af;">JM</span>
                  <span class="co-tech-av" style="background:#dcfce7;color:#166534;">LP</span>
                </div>
                <div class="co-amount">6 240 €</div>
                <span class="co-badge inprog">En cours</span>
              </div>
              <div class="co-row">
                <div class="co-ref">CH-2025-039</div>
                <div class="co-title">Installation PAC air/eau 12kW</div>
                <div class="co-client">M. Dubois · Caluire-et-Cuire</div>
                <div class="co-techs">
                  <span class="co-tech-av" style="background:#fef3c7;color:#92400e;">SB</span>
                </div>
                <div class="co-amount">8 900 €</div>
                <span class="co-badge to-bill">À facturer</span>
              </div>
              <div class="co-row late-ch">
                <div class="co-ref">CH-2025-031</div>
                <div class="co-title">Entretien annuel + remplacement corps de chauffe</div>
                <div class="co-client">Résidence Les Pins · Villeurbanne</div>
                <div class="co-techs">
                  <span class="co-tech-av" style="background:#dbeafe;color:#1e40af;">JM</span>
                </div>
                <div class="co-amount">1 450 €</div>
                <span class="co-badge late">Retard J+7</span>
              </div>
              <div class="co-row">
                <div class="co-ref">CH-2025-028</div>
                <div class="co-title">VMC double flux — Appartement T4</div>
                <div class="co-client">SCI Voltaire · Bron</div>
                <div class="co-techs">
                  <span class="co-tech-av" style="background:#dcfce7;color:#166534;">LP</span>
                  <span class="co-tech-av" style="background:#fef3c7;color:#92400e;">SB</span>
                </div>
                <div class="co-amount">3 200 €</div>
                <span class="co-badge closed">Clôturé</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== CHIFFRES ===== -->
      <section class="py-24 bg-gray-900">
        <div class="max-w-7xl mx-auto px-4">
          <h2 class="text-center text-3xl font-bold text-white mb-16">Termoflow vous donne le contrôle total sur vos chantiers</h2>
          <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div class="benefit-box-dark">
              <div class="benefit-val-dark">360°</div>
              <p class="benefit-lab-dark">Vue complète sur chaque chantier, de l'adresse à la clôture</p>
            </div>
            <div class="benefit-box-dark">
              <div class="benefit-val-dark">0</div>
              <p class="benefit-lab-dark">Double saisie entre terrain, bureau et facturation</p>
            </div>
            <div class="benefit-box-dark">
              <div class="benefit-val-dark">Temps réel</div>
              <p class="benefit-lab-dark">Planning, statuts et rentabilité mis à jour depuis le terrain</p>
            </div>
            <div class="benefit-box-dark">
              <div class="benefit-val-dark">+1 outil</div>
              <p class="benefit-lab-dark">De moins — tout remplace votre gestionnaire de chantiers actuel</p>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== CTA FINAL ===== -->
      <section class="py-24 text-center bg-white">
        <div class="max-w-3xl mx-auto px-4">
          <h2 class="text-4xl font-black text-gray-900 mb-6">
            Prenez le contrôle de vos chantiers.<br />
            <span class="gradient-text-blue">Dès aujourd'hui.</span>
          </h2>
          <p class="text-xl text-gray-500 mb-10">Du premier contact client à la dernière facture soldée — tout dans Termoflow.</p>
          <div class="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/contact"><button class="btn-primary-large">Demander une démonstration</button></a>
            <a href="/fonctionnalites" class="btn-outline-large">Autres fonctionnalités</a>
          </div>
        </div>
      </section>

      <landing-roi />
      <landing-avis />
    </main>

    <landing-footer />
  </div>
</template>

<script>
import LandingHeader from './LandingHeader.vue'
import LandingFooter from './LandingFooter.vue'
import LandingAvis from './LandingAvis.vue'
import LandingROI from './LandingROI.vue'

export default {
  name: 'ChantiersPage',
  components: {
    LandingHeader,
    LandingFooter,
    LandingAvis,
    'landing-roi': LandingROI
  }
}
</script>

<style scoped>
/* ==================== UTILS ==================== */
.gradient-text-blue {
  background: linear-gradient(to right, #2563eb, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.link-blue { color: #2563eb; font-weight: 700; text-decoration: none; }
.link-blue:hover { text-decoration: underline; }

/* ==================== HERO ==================== */
.hero-chantiers {
  padding: 10rem 0 6rem;
  background: radial-gradient(ellipse at top right, #eff6ff, transparent 50%),
              radial-gradient(ellipse at bottom left, #f5f3ff, transparent 50%);
}

.badge-chantier {
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: #eff6ff;
  color: #1d4ed8;
  font-weight: 800;
  font-size: 0.75rem;
  border-radius: 9999px;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  border: 1px solid #bfdbfe;
  letter-spacing: 0.05em;
}

.hero-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2.5rem;
  padding-top: 2rem;
  border-top: 1px solid #f1f5f9;
}
.stat-pill {
  display: flex;
  flex-direction: column;
  padding: 0.75rem 1.25rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  gap: 0.1rem;
}
.stat-val { font-size: 1.1rem; font-weight: 900; color: #2563eb; }
.stat-lab { font-size: 0.7rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; }

/* ==================== CHANTIER UI MOCKUP ==================== */
.chantier-ui {
  background: white;
  border-radius: 1.5rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}
.cui-header {
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.cui-header-left { display: flex; flex-direction: column; gap: 0.15rem; }
.cui-ref { font-size: 0.65rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.06em; }
.cui-title { font-size: 0.9rem; font-weight: 800; color: #1e293b; }
.cui-status {
  font-size: 0.68rem;
  font-weight: 800;
  padding: 0.3rem 0.8rem;
  border-radius: 9999px;
  white-space: nowrap;
}
.cui-status.en-cours { background: #dbeafe; color: #1d4ed8; }

.cui-body { padding: 1.25rem; display: flex; flex-direction: column; gap: 1.25rem; }

/* Map placeholder */
.cui-map-placeholder {
  background: #f1f5f9;
  border-radius: 1rem;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  position: relative;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}
.map-pin-anim { font-size: 2rem; color: #2563eb; }
.map-addr { display: flex; flex-direction: column; }
.map-addr strong { font-size: 0.9rem; font-weight: 800; color: #1e293b; }
.map-addr small { font-size: 0.72rem; color: #64748b; font-weight: 600; }
.map-link { position: absolute; bottom: 0.6rem; right: 0.9rem; font-size: 0.68rem; font-weight: 800; color: #2563eb; cursor: pointer; }
.map-link i { font-size: 0.6rem; }

/* Parties */
.cui-parties { display: flex; flex-direction: column; gap: 0.75rem; }
.cui-party { display: flex; align-items: center; gap: 0.85rem; }
.party-icon {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;
}
.party-icon.client { background: #dbeafe; color: #1d4ed8; }
.party-icon.regie { background: #f3e8ff; color: #7c3aed; }
.party-icon.locataire { background: #dcfce7; color: #166534; }
.cui-party { display: flex; flex-direction: row; align-items: center; gap: 0.75rem; }
.party-role { font-size: 0.65rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; display: block; }
.party-name { font-size: 0.85rem; font-weight: 800; color: #1e293b; display: block; }

/* Quick stats */
.cui-quick-stats {
  display: grid;
  grid-template-columns: repeat(4,1fr);
  gap: 0.75rem;
  background: #f8fafc;
  border-radius: 0.75rem;
  padding: 1rem;
}
.qs { text-align: center; }
.qs-val { display: block; font-size: 1.1rem; font-weight: 900; color: #2563eb; margin-bottom: 0.15rem; }
.qs-lab { font-size: 0.62rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em; }

/* ==================== TRUST BAR ==================== */
.trust-bar { background: #0f172a; padding: 1.25rem 0; }
.trust-items { display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 0; }
.trust-item { display: flex; align-items: center; gap: 0.6rem; padding: 0.5rem 2rem; color: #94a3b8; font-size: 0.8rem; font-weight: 700; }
.trust-item i { color: #2563eb; font-size: 0.9rem; }
.trust-sep { width: 1px; height: 1.25rem; background: #334155; }

/* ==================== SECTION COMMONS ==================== */
.section-label {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #2563eb;
  margin-bottom: 1rem;
}
.section-label-light {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #60a5fa;
  margin-bottom: 1rem;
}
.section-title { font-size: 2.25rem; font-weight: 900; color: #111827; line-height: 1.2; margin-bottom: 1rem; }
.section-sub { font-size: 1.05rem; color: #6b7280; line-height: 1.7; max-width: 38rem; }

/* ==================== MAP & CONTACTS ==================== */
.contact-features { display: flex; flex-direction: column; gap: 1.5rem; }
.contact-feat { display: flex; gap: 1.25rem; }
.cf-icon {
  width: 2.75rem;
  height: 2.75rem;
  flex-shrink: 0;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}
.cf-icon.map { background: #eff6ff; color: #2563eb; }
.cf-icon.client { background: #eff6ff; color: #1d4ed8; }
.cf-icon.regie { background: #f5f3ff; color: #7c3aed; }
.cf-icon.locataire { background: #f0fdf4; color: #166534; }
.contact-feat h4 { font-size: 0.95rem; font-weight: 800; color: #111827; margin-bottom: 0.4rem; }
.contact-feat p { font-size: 0.87rem; color: #6b7280; line-height: 1.6; }

.map-contacts-card {
  background: white;
  border-radius: 1.5rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

/* SVG-like map */
.map-visual { height: 200px; position: relative; overflow: hidden; background: #e8f0fe; }
.map-bg { width: 100%; height: 100%; position: relative; }
.map-road { position: absolute; background: white; opacity: 0.7; }
.map-road.h { top: 50%; left: 0; right: 0; height: 10px; transform: translateY(-50%); }
.map-road.v { left: 40%; top: 0; bottom: 0; width: 10px; }
.map-road.h2 { top: 75%; left: 0; right: 0; height: 6px; opacity: 0.5; }
.map-road.v2 { left: 70%; top: 0; bottom: 0; width: 6px; opacity: 0.5; }
.map-block { position: absolute; background: #c7d9f7; border-radius: 2px; }
.map-block.b1 { top: 10%; left: 5%; width: 30%; height: 35%; }
.map-block.b2 { top: 10%; left: 45%; width: 20%; height: 35%; }
.map-block.b3 { top: 60%; left: 5%; width: 30%; height: 30%; }
.map-block.b4 { top: 60%; left: 45%; width: 50%; height: 30%; }
.map-pin-wrap { position: absolute; top: 35%; left: 37%; transform: translate(-50%,-100%); z-index: 2; }
.map-pin-dot {
  font-size: 2.5rem;
  color: #2563eb;
  filter: drop-shadow(0 2px 6px rgba(37,99,235,0.4));
  display: flex;
  justify-content: center;
}
.map-tooltip {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.6rem 0.9rem;
  min-width: 180px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  margin-top: 0.25rem;
}
.map-tooltip strong { display: block; font-size: 0.8rem; font-weight: 800; color: #1e293b; }
.map-tooltip span { display: block; font-size: 0.7rem; color: #64748b; font-weight: 600; }
.map-itinerary { display: block; font-size: 0.68rem; font-weight: 800; color: #2563eb; margin-top: 0.35rem; cursor: pointer; }
.map-itinerary i { font-size: 0.6rem; }

.contacts-list { padding: 0.5rem 0; }
.contact-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.9rem 1.25rem;
  border-bottom: 1px solid #f8fafc;
}
.contact-avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 800;
  flex-shrink: 0;
}
.client-av { background: #dbeafe; color: #1d4ed8; }
.regie-av { background: #f3e8ff; color: #7c3aed; }
.locataire-av { background: #dcfce7; color: #166534; }
.contact-info { flex: 1; }
.contact-role { font-size: 0.6rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; display: block; }
.contact-name { font-size: 0.85rem; font-weight: 800; color: #1e293b; display: block; }
.contact-detail { font-size: 0.72rem; color: #64748b; font-weight: 600; display: block; }
.contact-actions { display: flex; gap: 0.5rem; }
.ca-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  background: white;
  color: #64748b;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

/* ==================== INTERVENTIONS ==================== */
.interv-features { display: flex; flex-direction: column; gap: 1.5rem; }
.interv-feat { display: flex; gap: 1.25rem; }
.if-icon {
  width: 2.75rem;
  height: 2.75rem;
  flex-shrink: 0;
  border-radius: 0.75rem;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}
.interv-feat h4 { font-size: 0.95rem; font-weight: 800; color: #111827; margin-bottom: 0.4rem; }
.interv-feat p { font-size: 0.87rem; color: #6b7280; line-height: 1.6; }

.interventions-list-card {
  background: white;
  border-radius: 1.5rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}
.ilc-header {
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.75rem;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.ilc-add {
  background: #2563eb;
  color: white;
  font-size: 0.72rem;
  font-weight: 800;
  padding: 0.35rem 0.9rem;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
}

.interv-item {
  display: flex;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f8fafc;
  align-items: flex-start;
}
.interv-status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 0.35rem;
}
.interv-status-dot.done { background: #22c55e; }
.interv-status-dot.inprog { background: #2563eb; box-shadow: 0 0 0 3px #dbeafe; }
.interv-status-dot.planned { background: #94a3b8; border: 2px solid #94a3b8; background: transparent; }

.interv-content { flex: 1; }
.interv-top { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.4rem; flex-wrap: wrap; }
.interv-type {
  font-size: 0.65rem;
  font-weight: 800;
  padding: 0.25rem 0.7rem;
  border-radius: 9999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.interv-type.depannage { background: #fef2f2; color: #991b1b; }
.interv-type.installation { background: #eff6ff; color: #1d4ed8; }
.interv-date { font-size: 0.72rem; color: #94a3b8; font-weight: 600; }
.interv-title { font-size: 0.88rem; font-weight: 800; color: #1e293b; margin-bottom: 0.5rem; }
.interv-meta { display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 0.6rem; }
.interv-meta span { font-size: 0.72rem; font-weight: 700; color: #64748b; }
.interv-meta i { margin-right: 0.3rem; }
.interv-footer-row { display: flex; align-items: center; justify-content: space-between; }
.interv-badge {
  font-size: 0.65rem;
  font-weight: 800;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.done-badge { background: #f0fdf4; color: #166534; }
.inprog-badge { background: #dbeafe; color: #1d4ed8; }
.planned-badge { background: #f1f5f9; color: #64748b; }
.interv-link { font-size: 0.72rem; font-weight: 800; color: #2563eb; text-decoration: none; }
.ilc-footer { padding: 1rem 1.5rem; text-align: center; }
.ilc-see-all { font-size: 0.82rem; font-weight: 800; color: #2563eb; text-decoration: none; }

/* ==================== SECTION DARK ==================== */
.section-dark { background: #0f172a; }

/* ==================== PLANNING ==================== */
.planning-mockup {
  background: #1e293b;
  border-radius: 1.5rem;
  border: 1px solid #334155;
  overflow: hidden;
  margin-bottom: 2.5rem;
}
.planning-header {
  background: #0f172a;
  border-bottom: 1px solid #334155;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}
.planning-nav { display: flex; align-items: center; gap: 0.75rem; }
.pnav-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid #334155;
  background: transparent;
  color: #64748b;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.pnav-title { font-size: 0.85rem; font-weight: 800; color: #f1f5f9; }
.planning-legend { display: flex; gap: 1.25rem; flex-wrap: wrap; }
.leg-item { display: flex; align-items: center; gap: 0.5rem; font-size: 0.75rem; font-weight: 700; color: #94a3b8; }
.leg-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.leg-dot.marchand { background: #3b82f6; }
.leg-dot.petit { background: #22c55e; }
.leg-dot.bernard { background: #f59e0b; }

.planning-grid { padding: 1.25rem; overflow-x: auto; }
.pg-days {
  display: grid;
  grid-template-columns: 120px repeat(5, 1fr);
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  padding-left: 0;
}
.pg-day-label {
  grid-column: auto;
  text-align: center;
  font-size: 0.72rem;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.pg-days { padding-left: 128px; }

.pg-technicians { display: flex; flex-direction: column; gap: 0.75rem; }
.pgt-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 0.5rem;
  align-items: stretch;
}
.pgt-name {
  font-size: 0.78rem;
  font-weight: 800;
  color: #94a3b8;
  display: flex;
  align-items: center;
  padding-right: 0.75rem;
}
.pgt-cells { display: grid; grid-template-columns: repeat(5,1fr); gap: 0.5rem; }
.pgt-cell { min-height: 80px; }
.empty-cell { background: rgba(255,255,255,0.02); border-radius: 0.5rem; border: 1px dashed #334155; }

.pgt-event {
  border-radius: 0.5rem;
  padding: 0.6rem 0.75rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.marchand-ev { background: rgba(59,130,246,0.15); border: 1px solid rgba(59,130,246,0.3); }
.petit-ev { background: rgba(34,197,94,0.12); border: 1px solid rgba(34,197,94,0.25); }
.bernard-ev { background: rgba(245,158,11,0.12); border: 1px solid rgba(245,158,11,0.25); }
.active-ev { border-width: 2px !important; }
.marchand-ev.active-ev { border-color: #3b82f6; }
.petit-ev.active-ev { border-color: #22c55e; }
.planned-ev { opacity: 0.7; }

.pev-badge { font-size: 0.6rem; font-weight: 800; color: #60a5fa; }
.planned-badge-ev { color: #94a3b8; }
.pev-title { font-size: 0.72rem; font-weight: 800; color: #f1f5f9; line-height: 1.3; }
.pev-addr { font-size: 0.65rem; color: #64748b; font-weight: 600; }
.pev-time { font-size: 0.65rem; color: #94a3b8; font-weight: 700; margin-top: auto; }

.planning-feats-grid {
  display: grid;
  grid-template-columns: repeat(4,1fr);
  gap: 1.25rem;
}
@media (max-width: 900px) { .planning-feats-grid { grid-template-columns: 1fr 1fr; } }
@media (max-width: 600px) { .planning-feats-grid { grid-template-columns: 1fr; } }

.pf-card {
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 1.25rem;
  padding: 1.75rem;
}
.pf-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  background: rgba(37,99,235,0.15);
  color: #60a5fa;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  margin-bottom: 1rem;
}
.pf-card h4 { font-size: 0.9rem; font-weight: 800; color: #f1f5f9; margin-bottom: 0.5rem; }
.pf-card p { font-size: 0.82rem; color: #64748b; line-height: 1.6; }

/* ==================== DOCUMENTS ==================== */
.doc-features { display: flex; flex-direction: column; gap: 1.5rem; }
.doc-feat { display: flex; gap: 1.25rem; }
.df-icon {
  width: 2.75rem;
  height: 2.75rem;
  flex-shrink: 0;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}
.df-icon.generated { background: #eff6ff; color: #2563eb; }
.df-icon.upload { background: #f0fdf4; color: #16a34a; }
.df-icon.share { background: #f5f3ff; color: #7c3aed; }
.df-icon.secure { background: #fff7ed; color: #d97706; }
.doc-feat h4 { font-size: 0.95rem; font-weight: 800; color: #111827; margin-bottom: 0.4rem; }
.doc-feat p { font-size: 0.87rem; color: #6b7280; line-height: 1.6; }

.documents-card {
  background: white;
  border-radius: 1.5rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}
.dc-header {
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.75rem;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.dc-upload-btn {
  background: #2563eb;
  color: white;
  font-size: 0.72rem;
  font-weight: 800;
  padding: 0.4rem 0.9rem;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.dc-filters {
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 1rem;
}
.dc-filter {
  font-size: 0.72rem;
  font-weight: 700;
  color: #94a3b8;
  padding: 0.7rem 1rem;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
}
.dc-filter.active { color: #2563eb; border-bottom-color: #2563eb; }

.doc-list { padding: 0.25rem 0; }
.doc-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.8rem 1.5rem;
  border-bottom: 1px solid #f8fafc;
}
.doc-item.uploaded-item { background: #fafffe; }
.doc-icon {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;
}
.devis-icon { background: #eff6ff; color: #2563eb; }
.facture-icon { background: #fff7ed; color: #d97706; }
.rapport-icon { background: #f0fdf4; color: #16a34a; }
.cerfa-icon { background: #fef2f2; color: #dc2626; }
.upload-icon { background: #f5f3ff; color: #7c3aed; }

.doc-info { flex: 1; min-width: 0; }
.doc-name { font-size: 0.8rem; font-weight: 700; color: #1e293b; display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.doc-meta { font-size: 0.68rem; color: #94a3b8; font-weight: 600; }
.doc-actions { display: flex; gap: 0.4rem; }
.doc-btn {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.4rem;
  border: 1px solid #e5e7eb;
  background: white;
  color: #64748b;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.dc-footer {
  background: #f8fafc;
  border-top: 1px solid #e5e7eb;
  padding: 0.85rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.75rem;
  font-weight: 700;
  color: #94a3b8;
}
.dc-dl-btn {
  background: white;
  border: 1px solid #e5e7eb;
  font-size: 0.72rem;
  font-weight: 800;
  padding: 0.4rem 0.9rem;
  border-radius: 9999px;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

/* ==================== RENTABILITÉ ==================== */
.rentabilite-layout {
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 2rem;
  align-items: start;
}
@media (max-width: 900px) { .rentabilite-layout { grid-template-columns: 1fr; } }

.renta-dashboard {
  background: white;
  border-radius: 1.5rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}
.renta-header {
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.5rem;
  font-size: 0.75rem;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.renta-main-kpi { padding: 1.5rem; border-bottom: 1px solid #f1f5f9; }
.renta-gauge-wrap { display: flex; justify-content: center; margin-bottom: 1rem; }
.renta-gauge { position: relative; width: 140px; height: 80px; }
.gauge-svg { width: 140px; height: 80px; }
.gauge-val { font-size: 1.75rem; font-weight: 900; color: #f97316; display: block; }
.gauge-sub { font-size: 0.65rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; }
.gauge-label { position: absolute; bottom: 0; left: 50%; transform: translateX(-50%); text-align: center; }
.renta-status {
  text-align: center;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
}
.renta-status.good { background: #f0fdf4; color: #166534; }
.renta-kpis { padding: 1rem 1.5rem; display: flex; flex-direction: column; gap: 0; border-bottom: 1px solid #f1f5f9; }
.renta-kpi {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.65rem 0;
  border-bottom: 1px solid #f8fafc;
}
.rk-label { font-size: 0.8rem; font-weight: 700; color: #6b7280; }
.rk-val { font-size: 0.85rem; font-weight: 800; color: #1e293b; }
.rk-val.green { color: #16a34a; }
.rk-val.orange { color: #f97316; }
.rk-val.blue { color: #2563eb; }

.renta-progress-section { padding: 1.25rem 1.5rem; }
.rp-label { font-size: 0.72rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.75rem; }
.rp-bar { height: 8px; border-radius: 9999px; background: #f1f5f9; overflow: hidden; display: flex; margin-bottom: 0.75rem; }
.rp-fill { height: 100%; }
.rp-fill.encaisse { background: #16a34a; }
.rp-fill.reste { background: #dbeafe; }
.rp-legend { display: flex; gap: 1.5rem; flex-wrap: wrap; }
.rp-legend span { font-size: 0.72rem; font-weight: 700; color: #6b7280; display: flex; align-items: center; gap: 0.4rem; }
.rp-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.rp-dot.encaisse-dot { background: #16a34a; }
.rp-dot.reste-dot { background: #93c5fd; }

/* Renta features */
.renta-features {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
}
@media (max-width: 600px) { .renta-features { grid-template-columns: 1fr; } }
.rf-card {
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 1.25rem;
  padding: 1.75rem;
}
.rf-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  margin-bottom: 1rem;
}
.rf-card h4 { font-size: 0.9rem; font-weight: 800; color: #111827; margin-bottom: 0.5rem; }
.rf-card p { font-size: 0.82rem; color: #6b7280; line-height: 1.6; }

/* ==================== OVERVIEW ==================== */
.chantiers-overview {
  background: white;
  border-radius: 1.5rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}
.co-filters {
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 1.5rem;
  display: flex;
  gap: 0;
  overflow-x: auto;
}
.cof {
  font-size: 0.75rem;
  font-weight: 700;
  color: #94a3b8;
  padding: 0.9rem 1.25rem;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
}
.cof.active { color: #2563eb; border-bottom-color: #2563eb; }
.co-list { padding: 0; }
.co-row {
  display: grid;
  grid-template-columns: 100px 1fr 1fr 80px 90px 90px;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f8fafc;
}
.co-row.active-ch { background: #fafbff; border-left: 3px solid #2563eb; }
.co-row.late-ch { background: #fffbfa; border-left: 3px solid #ef4444; }
.co-ref { font-size: 0.72rem; font-weight: 800; color: #94a3b8; font-family: monospace; }
.co-title { font-size: 0.85rem; font-weight: 800; color: #1e293b; }
.co-client { font-size: 0.75rem; color: #64748b; font-weight: 600; }
.co-techs { display: flex; gap: -0.25rem; }
.co-tech-av {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  font-size: 0.6rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}
.co-amount { font-size: 0.9rem; font-weight: 800; color: #1e293b; }
.co-badge {
  font-size: 0.65rem;
  font-weight: 800;
  padding: 0.3rem 0.75rem;
  border-radius: 9999px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.co-badge.inprog { background: #dbeafe; color: #1d4ed8; }
.co-badge.to-bill { background: #fff7ed; color: #c2410c; }
.co-badge.late { background: #fef2f2; color: #991b1b; }
.co-badge.closed { background: #f1f5f9; color: #475569; }

/* ==================== BENEFIT BOXES ==================== */
.benefit-box-dark {
  background: #1e293b;
  padding: 2.5rem 2rem;
  border-radius: 1.5rem;
  text-align: center;
  border: 1px solid #334155;
}
.benefit-val-dark { font-size: 2.75rem; font-weight: 900; color: #2563eb; margin-bottom: 0.75rem; }
.benefit-lab-dark { font-size: 0.87rem; font-weight: 700; color: #64748b; line-height: 1.5; }

/* ==================== BUTTONS ==================== */
.btn-primary { background: linear-gradient(to right, #2563eb, #7c3aed); color: white; padding: 1rem 2.5rem; border-radius: 9999px; font-weight: 800; border: none; cursor: pointer; font-size: 0.95rem; }
.btn-primary-large { background: #2563eb; color: white; padding: 1.2rem 3rem; border-radius: 9999px; font-weight: 800; border: none; font-size: 1.1rem; cursor: pointer; }
.btn-outline-large { border: 2px solid #e5e7eb; padding: 1.2rem 3rem; border-radius: 9999px; font-weight: 800; color: #1f2937; text-decoration: none; display: inline-block; font-size: 1rem; }

/* ==================== RESPONSIVE ==================== */
@media (max-width: 1024px) {
  .hero-chantiers { padding-top: 8rem; text-align: center; }
  .chantier-ui { margin-top: 3rem; }
  .section-title { font-size: 1.75rem; }
  .co-row { grid-template-columns: 1fr 1fr auto; }
  .co-ref, .co-client { display: none; }
}
@media (max-width: 600px) {
  .pg-days, .pgt-cells { grid-template-columns: repeat(3,1fr); }
  .pgt-cells .pgt-cell:nth-child(4),
  .pgt-cells .pgt-cell:nth-child(5) { display: none; }
  .pg-days .pg-day-label:nth-child(4),
  .pg-days .pg-day-label:nth-child(5) { display: none; }
}
</style>